import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';

const InstituteCreditsInfoView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // API configuration
  const API_BASE_URL = process.env.REACT_APP_API_LMS;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;

  // Fetch credit allocation details from API
  const fetchCreditAllocationDetails = async (allocationId) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/credit-allocations/${allocationId}`, {
        method: 'GET',
        headers: {
          'Authorization': API_TOKEN,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const response_data = await response.json();
      
      // Extract data from the response structure
      const apiData = response_data.data && response_data.data.length > 0 ? response_data.data[0] : null;
      
      if (!apiData) {
        throw new Error('No data found for this allocation');
      }
      
      // Transform API data to match component structure
      const transformedData = {
        id: apiData.id,
        institute: apiData.institute_name,
        totalCredits: apiData.total_credits_allocated,
        creditsRemaining: apiData.credits_remaining,
        allocatedOn: apiData.allocated_on,
        status: 'Active', // Default status since not provided in API
        creditsUsed: apiData.total_credits_allocated - apiData.credits_remaining,
        allocationId: `CA-${apiData.id.toString().padStart(3, '0')}`,
        allocatedBy: 'Admin',
        institutionId: apiData.institution_id,
        createdAt: apiData.created_at,
        updatedAt: apiData.updated_at
      };

      setSelectedInstitute(transformedData);
    } catch (err) {
      console.error('Error fetching credit allocation details:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCreditAllocationDetails(id);
    }
  }, [id]);

  const handleBackToList = () => {
    navigate('/institute-credits');
  };

  const handleAllCredits = () => {
    navigate('/institute-credits');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <>
        <div className="row">
          <div className="col-12">
            <PageHeader title="Credit Allocation Info" 
              breadcrumbs={[
                { label: "Dashboard", path: "/institute-dashboard" },
                { label: "Credit Allocation Info", active: true }
              ]}
            />
          </div>
        </div>
        
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error || !selectedInstitute) {
    return (
      <>
        <div className="row">
          <div className="col-12">
            <PageHeader title="Credit Allocation Info" 
              breadcrumbs={[
                { label: "Dashboard", path: "/institute-dashboard" },
                { label: "Credit Allocation Info", active: true }
              ]}
            />
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center">
                  <h5>{error ? `Error: ${error}` : 'Credit allocation not found'}</h5>
                  <button 
                    className="btn btn-primary mt-3"
                    onClick={handleBackToList}
                  >
                    Back to List
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <PageHeader title="Credit Allocation Info" 
            breadcrumbs={[
              { label: "Dashboard", path: "/institute-dashboard" },
              { label: "Credit Allocation Info", active: true }
            ]}
          />
        </div>
      </div>
      
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">Credit Allocation Info - {selectedInstitute.institute}</h4>
            <button 
              className="btn btn-outline-primary"
              onClick={handleAllCredits}
              style={{ borderRadius: '6px' }}
            >
              <i className="fas fa-list me-2"></i>
              All Credit Allocations
            </button>
          </div>

          {/* Institute Credit Details Card */}
          <div className="card shadow-sm" style={{ border: 'none', borderRadius: '12px' }}>
            <div className="card-body p-4">
              <div className="credit-details">
                <div className="row mb-3">
                  <div className="col-md-3 col-sm-4">
                    <strong>Allocation ID</strong>
                  </div>
                  <div className="col-md-9 col-sm-8 text-muted">
                    {selectedInstitute.allocationId}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-3 col-sm-4">
                    <strong>Institute</strong>
                  </div>
                  <div className="col-md-9 col-sm-8 text-muted">
                    {selectedInstitute.institute}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-3 col-sm-4">
                    <strong>Institution ID</strong>
                  </div>
                  <div className="col-md-9 col-sm-8 text-muted">
                    {selectedInstitute.institutionId}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-3 col-sm-4">
                    <strong>Total Credits</strong>
                  </div>
                  <div className="col-md-9 col-sm-8 text-muted">
                    {selectedInstitute.totalCredits}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-3 col-sm-4">
                    <strong>Credits Used</strong>
                  </div>
                  <div className="col-md-9 col-sm-8 text-muted">
                    {selectedInstitute.creditsUsed}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-3 col-sm-4">
                    <strong>Credits Remaining</strong>
                  </div>
                  <div className="col-md-9 col-sm-8 text-muted">
                    <span className={selectedInstitute.creditsRemaining < 50 ? 'text-warning' : 'text-success'}>
                      {selectedInstitute.creditsRemaining}
                    </span>
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-3 col-sm-4">
                    <strong>Allocated On</strong>
                  </div>
                  <div className="col-md-9 col-sm-8 text-muted">
                    {formatDate(selectedInstitute.allocatedOn)}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-3 col-sm-4">
                    <strong>Allocated By</strong>
                  </div>
                  <div className="col-md-9 col-sm-8 text-muted">
                    {selectedInstitute.allocatedBy}
                  </div>
                </div>
                
                {/* <div className="row mb-3">
                  <div className="col-md-3 col-sm-4">
                    <strong>Created At</strong>
                  </div>
                  <div className="col-md-9 col-sm-8 text-muted">
                    {formatDate(selectedInstitute.createdAt)}
                  </div>
                </div> */}
                
                <div className="row mb-4">
                  <div className="col-md-3 col-sm-4">
                    <strong>Status</strong>
                  </div>
                  <div className="col-md-9 col-sm-8 text-muted">
                    <span className={`badge ${selectedInstitute.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
                      {selectedInstitute.status}
                    </span>
                  </div>
                </div>

                {/* Back to List Button */}
                <div className="row">
                  <div className="col-12">
                    <button 
                      className="btn btn-secondary"
                      onClick={handleBackToList}
                      style={{ borderRadius: '6px' }}
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      Back to List
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .credit-details .row {
          padding: 12px 0;
          border-bottom: 1px solid #f0f0f0;
          margin: 0;
        }
        .credit-details .row:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        .credit-details strong {
          color: #333;
          font-weight: 600;
          font-size: 14px;
        }
        .credit-details .text-muted {
          color: #666 !important;
          font-size: 14px;
        }
        .card {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
        }
        .btn {
          font-size: 14px;
          font-weight: 500;
        }
        .btn-outline-primary {
          border-color: #007bff;
          color: #007bff;
        }
        .btn-outline-primary:hover {
          background-color: #007bff;
          border-color: #007bff;
        }
        .btn-secondary {
          background-color: #6c757d;
          border-color: #6c757d;
        }
        .badge {
          font-size: 12px;
          padding: 4px 8px;
        }
        .text-warning {
          color: #ffc107 !important;
          font-weight: 600;
        }
        .text-success {
          color: #28a745 !important;
          font-weight: 600;
        }
        @media (max-width: 768px) {
          .credit-details .col-sm-4 {
            margin-bottom: 8px;
          }
        }
      `}</style>
    </>
  );
};

export default InstituteCreditsInfoView;